<template>
  <div
    class="new-fixed-asset-page new-fixed-asset-page--glass glass-tab-page"
    :class="{ 'new-fixed-asset-page--embedded': embedded }"
  >
    <BrucdcumHeader
      v-if="!embedded"
      :breadcrumb="['Fixed Assets', 'New Fixed Asset']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush new-fixed-asset-glass-shell">
      <div class="glass-tab-view__content new-fixed-asset-page-stack">
        <div v-if="!embedded" class="new-fixed-asset-header-block">
          <h2 class="new-fixed-asset-title">New Asset</h2>
          <p class="new-fixed-asset-subtitle">
            Register a fixed asset with purchase details, accounts, and depreciation settings
          </p>
        </div>

        <form class="new-fixed-asset-form" @submit.prevent="handleSubmit">
          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-3">
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-item-name"
                  label="New Item"
                  v-model="form.itemName"
                  flex
                  required
                  placeholder="New Item"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-asset-number"
                  label="Asset Number"
                  v-model="form.assetNumber"
                  flex
                  required
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDateField
                  input-id="new-fixed-asset-purchase-date"
                  label="Purchase Date"
                  v-model="form.purchaseDate"
                  flex
                  required
                  @change="handleDateChange"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-3">
              <div class="new-fixed-asset-form-field">
                <LabelDropdown
                  label="Account"
                  select-id="new-fixed-asset-account"
                  v-model="form.account"
                  flex
                  wide
                  searchable
                  :disabled="loadingAccounts"
                  :options="fixedAssetAccountOptions"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDropdown
                  label="Depreciation Account"
                  select-id="new-fixed-asset-depreciation-account"
                  v-model="form.depreciationAccount"
                  flex
                  wide
                  searchable
                  :disabled="loadingAccounts"
                  :options="depreciationAccountOptions"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDropdown
                  label="Asset Type"
                  select-id="new-fixed-asset-asset-type"
                  v-model="form.assetType"
                  flex
                  wide
                  searchable
                  :options="assetTypeOptions"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-3">
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-purchase-price"
                  label="Purchase Price (USD)"
                  v-model="form.purchasePrice"
                  type="number"
                  flex
                  step="0.01"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-cost-basis"
                  label="Cost Basis (USD)"
                  v-model="form.costBasis"
                  type="number"
                  flex
                  step="0.01"
                  placeholder="Total cost for depreciation"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDropdown
                  label="Acquired As"
                  select-id="new-fixed-asset-acquired-as"
                  v-model="form.acquiredAs"
                  flex
                  wide
                  :searchable="false"
                  :options="acquiredAsOptions"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-3">
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-vendor"
                  label="Vendor/Payee"
                  v-model="form.vendor"
                  flex
                  placeholder="Vendor or payee name"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-location"
                  label="Location"
                  v-model="form.location"
                  flex
                  placeholder="Physical location"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-serial-number"
                  label="Serial Number"
                  v-model="form.serialNumber"
                  flex
                  placeholder="Serial number"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-2">
              <div class="new-fixed-asset-form-field">
                <LabelInputField
                  input-id="new-fixed-asset-po-number"
                  label="Purchase Order Number"
                  v-model="form.purchaseOrderNumber"
                  flex
                  placeholder="PO Number"
                />
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDateField
                  input-id="new-fixed-asset-warranty-date"
                  label="Warranty Expiration Date"
                  v-model="form.warrantyExpirationDate"
                  flex
                  @change="handleDateChange"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--full">
              <div class="new-fixed-asset-form-field">
                <LabelDescriptionField
                  input-id="new-fixed-asset-description"
                  label="Description"
                  v-model="form.description"
                  flex
                  :rows="2"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="glass-tab-panel__row new-fixed-asset-form-row--cols-2">
              <div class="new-fixed-asset-form-field">
                <div class="new-fixed-asset-percent-field">
                  <LabelInputField
                    input-id="new-fixed-asset-depreciation-rate"
                    label="Depreciation Rate"
                    v-model="form.depreciationRate"
                    type="number"
                    flex
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div class="new-fixed-asset-form-field">
                <LabelDropdown
                  label="Depreciation Method"
                  select-id="new-fixed-asset-depreciation-method"
                  v-model="form.depreciationMethod"
                  flex
                  wide
                  searchable
                  :options="depreciationMethodOptions"
                />
              </div>
            </div>
          </div>

          <div class="new-fixed-asset-glass-panel glass-tab-panel--nested">
            <div class="new-fixed-asset-info-panel">
              <p
                v-if="isPurchaseDateBeforeStartDate()"
                class="new-fixed-asset-info-warning"
              >
                The purchase date is prior to the fixed assets start date. {{ fixedAssetsStartDate }}
              </p>
              <LabelInputField
                input-id="new-fixed-asset-accumulated-depreciation"
                :label="`Accumulated depreciation to ${accumulatedDepreciationDate} (USD)`"
                :model-value="calculatedAccumulatedDepreciation"
                flex
                readonly
              />
            </div>
          </div>

          <div class="new-fixed-asset-actions-panel glass-tab-panel--nested">
            <div class="new-fixed-asset-form-actions">
              <PrimaryButton label="Save and Close" type="button" @click="handleSaveAndClose" />
              <PrimaryButton label="Register" type="button" @click="handleRegister" />
              <SecondaryButton label="Cancel" type="button" @click="handleCancel" />
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/NewFixedAsset.css';
import '../styles/new-fixed-asset-glass.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const ACQUIRED_AS_OPTIONS = Object.freeze([
  { value: '', label: 'Select' },
  { value: 'New', label: 'New' },
  { value: 'Used', label: 'Used' },
]);

export default {
  name: 'NewFixedAsset',
  components: {
    BrucdcumHeader,
    LabelInputField,
    LabelDropdown,
    LabelDateField,
    LabelDescriptionField,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'saved'],
  data() {
    const getFormattedDate = (date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    const today = new Date();
    const currentDate = getFormattedDate(today);
    const fixedAssetsStartDate = getFormattedDate(new Date(today.getFullYear(), 0, 1));

    return {
      form: {
        itemName: '',
        assetNumber: '1',
        account: '',
        purchaseDate: currentDate,
        purchasePrice: 0,
        costBasis: '',
        vendor: '',
        acquiredAs: '',
        location: '',
        serialNumber: '',
        purchaseOrderNumber: '',
        warrantyExpirationDate: '',
        description: '',
        assetType: '',
        depreciationRate: 0,
        depreciationMethod: '',
        depreciationAccount: '',
        accumulatedDepreciation: '$ 0.00',
      },
      fixedAssetsStartDate,
      accumulatedDepreciationDate: currentDate,
      acquiredAsOptions: ACQUIRED_AS_OPTIONS,
      assetTypes: [
        'Vehicles - Fixed Asset',
        'Computer Equipment - Fixed Asset',
        'Furniture and Fixtures - Fixed Asset',
        'Machinery and Equipment - Fixed Asset',
        'Buildings - Fixed Asset',
        'Land - Fixed Asset',
        'Leasehold Improvements - Fixed Asset',
        'Office Equipment - Fixed Asset',
        'Software - Fixed Asset',
        'Tools and Equipment - Fixed Asset',
        'Construction Equipment - Fixed Asset',
        'Medical Equipment - Fixed Asset',
        'Other - Fixed Asset',
      ],
      depreciationMethods: [
        'Straight Line',
        'Declining Balance',
        'Double Declining Balance',
        'Sum of Years\' Digits',
        'Units of Production',
      ],
      fixedAssetAccounts: [],
      depreciationAccounts: [],
      loadingAccounts: false,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId;
    },
    backFallbackRoute() {
      const companyId = this.companyId || this.$route.params.id;
      return `/company/${companyId}/dashboard/company/fixed-assets/pending-items`;
    },
    fixedAssetAccountOptions() {
      return [
        { value: '', label: 'Select Account' },
        ...this.fixedAssetAccounts.map((account) => ({
          value: account.id,
          label: `${account.accountNumber} - ${account.accountName} - ${account.accountType}`,
        })),
      ];
    },
    depreciationAccountOptions() {
      return [
        { value: '', label: 'Select Depreciation Account' },
        ...this.depreciationAccounts.map((account) => ({
          value: account.id,
          label: `${account.accountNumber} - ${account.accountName} - ${account.accountType}`,
        })),
      ];
    },
    assetTypeOptions() {
      return [
        { value: '', label: 'Select Asset Type' },
        ...this.assetTypes.map((type) => ({ value: type, label: type })),
      ];
    },
    depreciationMethodOptions() {
      return [
        { value: '', label: 'Select Depreciation Method' },
        ...this.depreciationMethods.map((method) => ({ value: method, label: method })),
      ];
    },
    calculatedAccumulatedDepreciation() {
      const costBasis = this.form.costBasis ? parseFloat(this.form.costBasis) : 0;
      const purchasePrice = this.form.purchasePrice ? parseFloat(this.form.purchasePrice) : 0;

      const baseAmount = costBasis > 0 ? costBasis : purchasePrice;
      const depreciationRate = this.form.depreciationRate ? parseFloat(this.form.depreciationRate) : 0;

      if (!this.form.purchaseDate || baseAmount <= 0 || depreciationRate <= 0 || isNaN(baseAmount) || isNaN(depreciationRate)) {
        return '$ 0.00';
      }

      try {
        const purchaseDateParts = this.form.purchaseDate.split('/');
        if (purchaseDateParts.length !== 3) {
          return '$ 0.00';
        }

        const purchaseDate = new Date(
          parseInt(purchaseDateParts[2], 10),
          parseInt(purchaseDateParts[0], 10) - 1,
          parseInt(purchaseDateParts[1], 10),
        );

        const depDateParts = this.accumulatedDepreciationDate.split('/');
        if (depDateParts.length !== 3) {
          return '$ 0.00';
        }

        const depDate = new Date(
          parseInt(depDateParts[2], 10),
          parseInt(depDateParts[0], 10) - 1,
          parseInt(depDateParts[1], 10),
        );

        if (purchaseDate > depDate) {
          return '$ 0.00';
        }

        const annualDepreciation = baseAmount * (depreciationRate / 100);
        const monthsDiff = (depDate.getFullYear() - purchaseDate.getFullYear()) * 12
          + (depDate.getMonth() - purchaseDate.getMonth());
        const accumulatedDepreciation = (annualDepreciation / 12) * monthsDiff;
        const finalAmount = Math.min(accumulatedDepreciation, baseAmount);

        return `$ ${finalAmount.toFixed(2)}`;
      } catch (error) {
        logger.error('Error calculating accumulated depreciation:', error);
        return '$ 0.00';
      }
    },
  },
  async mounted() {
    await this.fetchAccounts();
  },
  methods: {
    isPurchaseDateBeforeStartDate() {
      if (!this.form.purchaseDate || !this.fixedAssetsStartDate) {
        return false;
      }

      const purchaseDateParts = this.form.purchaseDate.split('/');
      const startDateParts = this.fixedAssetsStartDate.split('/');

      if (purchaseDateParts.length !== 3 || startDateParts.length !== 3) {
        return false;
      }

      const purchaseDate = new Date(
        parseInt(purchaseDateParts[2], 10),
        parseInt(purchaseDateParts[0], 10) - 1,
        parseInt(purchaseDateParts[1], 10),
      );

      const startDate = new Date(
        parseInt(startDateParts[2], 10),
        parseInt(startDateParts[0], 10) - 1,
        parseInt(startDateParts[1], 10),
      );

      return purchaseDate < startDate;
    },
    handleDateChange(value) {
      logger.log('Date changed:', value);
    },
    validateDate(field) {
      const value = this.form[field];
      if (!value) return true;

      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!dateRegex.test(value)) return false;

      const parts = value.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day;
    },
    handleSubmit() {
      logger.log('Form submitted:', this.form);
    },
    validateRequiredFields() {
      if (!this.form.itemName || !this.form.itemName.trim()) {
        this.$toast?.error('Item Name is required') || alert('Item Name is required');
        return false;
      }
      if (!this.form.assetNumber || !this.form.assetNumber.trim()) {
        this.$toast?.error('Asset Number is required') || alert('Asset Number is required');
        return false;
      }
      if (this.form.purchaseDate && !this.validateDate('purchaseDate')) {
        this.$toast?.error('Please enter a valid date in MM/DD/YYYY format for Purchase Date')
          || alert('Please enter a valid date in MM/DD/YYYY format for Purchase Date');
        return false;
      }
      return true;
    },
    handleSaveAndClose() {
      if (!this.validateRequiredFields()) return;

      logger.log('Save and Close:', this.form);
      this.$toast?.success('Fixed asset saved successfully') || alert('Fixed asset saved successfully');
      this.$emit('saved');
      if (this.embedded) {
        this.$emit('cancel');
      } else {
        this.$router.go(-1);
      }
    },
    handleRegister() {
      if (!this.validateRequiredFields()) return;

      logger.log('Register:', this.form);
      this.$toast?.success('Fixed asset registered successfully') || alert('Fixed asset registered successfully');
    },
    handleCancel() {
      this.$emit('cancel');
      if (!this.embedded) {
        this.$router.go(-1);
      }
    },
    async fetchAccounts() {
      if (!this.companyId) {
        logger.warn('Company ID not found in route params');
        return;
      }

      this.loadingAccounts = true;
      try {
        const response = await ApiService.get(`/company-account/company/${this.companyId}`);
        const accountsData = Array.isArray(response.data)
          ? response.data
          : (response.data?.accounts || response.data?.data || []);

        this.fixedAssetAccounts = accountsData
          .filter((acc) => acc.active !== false && acc.accountType === 'Fixed Asset')
          .map((acc) => ({
            id: acc.id,
            accountName: acc.accountName || acc.name,
            accountNumber: acc.accountNumber,
            accountType: acc.accountType,
          }))
          .sort((a, b) => (a.accountName || '').localeCompare(b.accountName || ''));

        const expenseTypes = ['Expense', 'Other Expense'];
        this.depreciationAccounts = accountsData
          .filter((acc) => acc.active !== false
            && (expenseTypes.includes(acc.accountType) || acc.isDepreciation === true))
          .map((acc) => ({
            id: acc.id,
            accountName: acc.accountName || acc.name,
            accountNumber: acc.accountNumber,
            accountType: acc.accountType,
          }))
          .sort((a, b) => (a.accountName || '').localeCompare(b.accountName || ''));
      } catch (error) {
        logger.error('Error fetching accounts:', error);
        this.$toast?.error('Failed to load accounts') || alert('Failed to load accounts');
        this.fixedAssetAccounts = [];
        this.depreciationAccounts = [];
      } finally {
        this.loadingAccounts = false;
      }
    },
  },
};
</script>
